import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useInputMenu } from '../hooks/useInputMenu';
import { MenuId, TriggerSource } from '../types';

interface InputMenuContextValue {
  isMenuOpen: (menuId: MenuId) => boolean;
  wasOpenedBy: (menuId: MenuId, triggeredBy: TriggerSource) => boolean;
  openMenu: (menuId: MenuId, triggeredBy: TriggerSource, metadata?: any) => void;
  closeMenu: (menuId: MenuId) => void;
  toggleMenu: (menuId: MenuId, triggeredBy: TriggerSource, metadata?: any) => void;
  closeAllMenus: () => void;
  smartCloseMenu: (menuId: MenuId, requestedBy?: TriggerSource) => void;
  registerMenuRef: (menuId: MenuId, ref: React.RefObject<HTMLElement | null>) => void;
}

export const InputMenuContext = createContext<InputMenuContextValue | null>(null);

export function useInputMenuContext() {
  const context = useContext(InputMenuContext);
  if (!context) {
    throw new Error('useInputMenuContext must be used within InputMenuProvider');
  }
  return context;
}

interface InputMenuProviderProps {
  children: React.ReactNode;
  onMenuChange?: (menuId: MenuId, state: any) => void;
  clickOutsideSelector?: string;
}

export function InputMenuProvider({ 
  children, 
  onMenuChange,
  clickOutsideSelector = '[data-chat-component]'
}: InputMenuProviderProps) {
  const menuManager = useInputMenu({ onMenuChange, exclusiveMenus: true });
  const menuRefs = useRef<Record<string, React.RefObject<HTMLElement | null>>>({});
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking on a portal dropdown (e.g., SecondaryTag dropdown)
      if (target.closest('[data-dropdown-portal="true"]')) {
        return;
      }
      
      const clickedButton = target.closest('button');
      if (clickedButton) {
        const isProtectedComponent = clickedButton.closest(clickOutsideSelector);
        
        if (isProtectedComponent) {
          return;
        }
      }
      
      Object.entries(menuManager.menuStates).forEach(([menuId, state]) => {
        if (state.isOpen) {
          const menuRef = menuRefs.current[menuId];
          if (menuRef?.current && !menuRef.current.contains(target)) {
            menuManager.closeMenu(menuId as MenuId);
          }
        }
      });
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuManager.menuStates, menuManager.closeMenu, clickOutsideSelector]);
  
  const registerMenuRef = (menuId: MenuId, ref: React.RefObject<HTMLElement | null>) => {
    menuRefs.current[menuId] = ref;
  };
  
  const contextValue: InputMenuContextValue = {
    isMenuOpen: menuManager.isMenuOpen,
    wasOpenedBy: menuManager.wasOpenedBy,
    openMenu: menuManager.openMenu,
    closeMenu: menuManager.closeMenu,
    toggleMenu: menuManager.toggleMenu,
    closeAllMenus: menuManager.closeAllMenus,
    smartCloseMenu: menuManager.smartCloseMenu,
    registerMenuRef
  };
  
  return (
    <InputMenuContext.Provider value={contextValue}>
      {children}
    </InputMenuContext.Provider>
  );
}

